import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "./MyTechProfileAchievements.css";
import MyTechProfileThirdParty from "./MyTechProfileThirdparty";
import AnimatedGradientText from "./AnimatedGradient";
import { useDispatch, useSelector } from "react-redux";
import { setProfileState } from "../../../actions/MyTechProfile/myTechProfileActions";
import useWindowResize from "../../../utils/WindowResize";

// --- CACHING UTILITY ---
const TTL = 24 * 60 * 60 * 1000; // 24 hours

const cache = {
  set: (key, data) => {
    try {
      const item = {
        data: data,
        expiry: new Date().getTime() + TTL,
      };
      localStorage.setItem(key, JSON.stringify(item));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  },
  get: (key) => {
    try {
      const itemStr = localStorage.getItem(key);
      if (!itemStr) return null;
      const item = JSON.parse(itemStr);
      const now = new Date().getTime();
      if (now > item.expiry) {
        localStorage.removeItem(key);
        return null;
      }
      return item.data;
    } catch (error) {
      console.error("Error reading from localStorage:", error);
      return null;
    }
  },
};

// --- DEFAULT GITHUB STATS ---
const DEFAULT_GITHUB_STATS = {
  totalRepos: 42,
  languages: [
    { name: "JavaScript", value: 65.5 },
    { name: "TypeScript", value: 25.2 },
    { name: "HTML", value: 5.1 },
    { name: "CSS", value: 4.2 },
    { name: "Python", value: 2.0 },
    { name: "Shell", value: 1.0 },
  ],
};

const getGitHubData = async (username) => {
  if (!username) return null;
  const cacheKey = `github_${username}`;
  const cachedData = cache.get(cacheKey);

  if (cachedData) {
    console.log(
      `[Cache HIT] Returning cached data for GitHub user: ${username}`
    );
    return cachedData;
  }

  console.log(`[Cache MISS] Fetching fresh data for GitHub user: ${username}`);
  const token = process.env.REACT_APP_GITHUB_TOKEN;
  const headers = token ? { Authorization: `token ${token}` } : {};

  try {
    const reposResponse = await axios.get(
      `https://api.github.com/users/${username}/repos?per_page=100`,
      { headers }
    );
    const repos = reposResponse.data;
    const languagePromises = repos.map((repo) =>
      axios.get(repo.languages_url, { headers }).catch(() => ({ data: {} }))
    );
    const languageResponses = await Promise.all(languagePromises);

    const langStats = languageResponses.reduce((acc, response) => {
      for (const lang in response.data) {
        acc[lang] = (acc[lang] || 0) + response.data[lang];
      }
      return acc;
    }, {});

    const totalBytes = Object.values(langStats).reduce(
      (sum, count) => sum + count,
      0
    );
    const langData = Object.keys(langStats).map((lang) => ({
      name: lang,
      value: totalBytes > 0 ? (langStats[lang] / totalBytes) * 100 : 0,
    }));

    langData.sort((a, b) => b.value - a.value);
    const result = { totalRepos: repos.length, languages: langData };
    cache.set(cacheKey, result);
    return result;
  } catch (error) {
    console.error("Error fetching GitHub data:", error);
    return null;
  }
};

// --- UI Components ---
const GitHubChart = ({ data }) => (
  <ResponsiveContainer width="100%" height={300}>
    <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
      <XAxis dataKey="name" stroke="#a0aec0" />
      <YAxis
        stroke="#a0aec0"
        unit="%"
        domain={[0, 100]}
        ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
        allowDecimals={false}
      />
      <Tooltip
        formatter={(value) => `${value.toFixed(2)}%`}
        contentStyle={{
          backgroundColor: "rgba(30, 41, 59, 0.9)",
          borderColor: "#8a2be2",
        }}
        labelStyle={{ color: "#a0aec0" }}
        itemStyle={{ color: "#8a2be2" }}
        cursor={false}
      />
      <Legend />
      <Bar
        dataKey="value"
        fill="#8a2be2"
        name="Language Usage"
        className="custom-bar"
      />
    </BarChart>
  </ResponsiveContainer>
);

const GitHubContributionGraph = ({ username }) => (
  <div className="mytechprofileachievements__contribution-graph">
    <img
      src={`https://ghchart.rshah.org/8A2BE2/${username}`}
      alt={`${username}'s Contribution Graph`}
      style={{ width: "100%", maxWidth: "800px" }}
    />
  </div>
);

const InfoCard = () => (
  <div className="mytechprofileachievements__info-card">
    <h3>No Achievements to Display</h3>
    <p>
      Please add your GitHub profile or other achievements to see them here.
    </p>
  </div>
);

// --- MAIN COMPONENT ---
const MyTechProfileAchievements = ({ technicalAchievementsRef }) => {
  const { profile } = useSelector((state) => state.editMyTechProfileStore);
  const dispatch = useDispatch();

  const platforms = profile?.technicalAchievements?.platforms || [];
  const githubPlatform = platforms.find((p) => p.id === "github");
  const githubUsername = githubPlatform?.username || "";

  const [githubData, setGithubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("github");
  const isSmallScreen = useWindowResize();

  const showAchievements = platforms.length > 0;

  useEffect(() => {
    const fetchData = async () => {
      if (activeTab !== "github" || !githubUsername) {
        setGithubData(null);
        setLoading(false);
        return;
      }

      setLoading(true);

      if (githubUsername === "your-username") {
        console.log("[Default] Using default stats for 'your-username'.");
        setGithubData(DEFAULT_GITHUB_STATS);
        setLoading(false);
      } else {
        const ghData = await getGitHubData(githubUsername);
        setGithubData(ghData);
        setLoading(false);
      }
    };
    fetchData();
  }, [githubUsername, activeTab]);

  useEffect(() => {
    const currentTab = profile?.technicalAchievements?.currentActiveTab;
    if (currentTab) {
      setActiveTab(currentTab);
    } else if (platforms.length > 0) {
      // Set the first available platform as the active tab
      setActiveTab(platforms[0].id);
    }
  }, [profile, platforms]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    dispatch(
      setProfileState({
        ...profile,
        technicalAchievements: {
          ...profile.technicalAchievements,
          currentActiveTab: tab,
        },
      })
    );
  };

  const renderContent = () => {
    if (loading && activeTab === "github") {
      return (
        <p className="mytechprofileachievements__loading">
          Fetching latest stats...
        </p>
      );
    }
    switch (activeTab) {
      case "github":
        if (githubData && githubUsername) {
          return (
            <div className="mytechprofileachievements__section">
              <h2 className="mytechprofileachievements__subtitle">
                GitHub Statistics
              </h2>
              <p className="mytechprofileachievements__stat">
                GitHub Username: <strong>{githubUsername}</strong>
              </p>
              <p className="mytechprofileachievements__stat">
                Total Public Repositories: {githubData.totalRepos}
              </p>
              <GitHubChart data={githubData.languages.slice(0, 7)} />
              {!isSmallScreen && (
                <GitHubContributionGraph username={githubUsername} />
              )}
            </div>
          );
        } else if (githubUsername) {
          return (
            <div className="mytechprofileachievements__section">
              <h2 className="mytechprofileachievements__subtitle">
                GitHub Statistics
              </h2>
              <p>Could not load GitHub data. Please check the username.</p>
            </div>
          );
        }
        return null;
      case "leetcode":
      case "gfg":
        return <MyTechProfileThirdParty />;
      default:
        return null;
    }
  };

  return (
    <div
      ref={technicalAchievementsRef}
      className="mytechprofileachievements__container"
    >
      <div style={{ textAlign: "center" }}>
        <h3 className="mytechprofileachievements_subtitle">
          My Technical Forte
        </h3>
        <h1 className="mytechprofileachievements__title">
          Technical <AnimatedGradientText text={"Achievements"} />
        </h1>
      </div>

      {showAchievements ? (
        <>
          <div className="mytechprofileachievements__tabs">
            {platforms.map((platform) => (
              <div
                key={platform.id}
                className={`mytechprofileachievements__tab ${
                  activeTab === platform.id ? "selected" : ""
                }`}
                onClick={() => handleTabClick(platform.id)}
              >
                {platform.platformName}
              </div>
            ))}
          </div>
          <div className="mytechprofileachievements__content">
            {renderContent()}
          </div>
        </>
      ) : (
        <InfoCard />
      )}
    </div>
  );
};

export default MyTechProfileAchievements;
